import styles from "./dropIndicator.module.scss";

/** Same vocabulary as `RowIndicator["side"]` (`useDropTarget.ts`) — kept separate so this file has
 *  no dependency on `useDropTarget`'s own types, since a consumer can drive this from something
 *  other than a `rowIndicator` (e.g. FileExplorer's OS-upload highlight, driven by its own hover
 *  state, not a drag-and-drop core `rowIndicator`). */
export type ElementIndicatorSide = "above" | "below" | "inside";

/** "start"/"end" of the whole displayed list — no previous row for an "above", no next for a
 *  "below" — see `.indicatorEdge` in `dropIndicator.module.scss` for why this needs its own class. */
export type ElementIndicatorEdge = "start" | "end";

const ALL_CLASSES = [
  styles.indicatorAbove,
  styles.indicatorBelow,
  styles.indicatorInsideSection,
  styles.indicatorInsideSectionLeft,
  styles.indicatorInsideSectionRight,
  styles.indicatorEdge,
];

/**
 * Applies the shared drop-indicator classes (`dropIndicator.module.scss`) directly onto a set of
 * DOM elements — the single implementation behind every "attached to the element" indicator that
 * can't bind `:class` in a Vue template, because the elements aren't Vue-owned or are recycled
 * (ag-Grid's virtualized rows) or are driven by imperative event listeners rather than reactive
 * state (FileExplorer's OS-upload hover highlight).
 *
 * `elements` is every DOM section belonging to one logical row, in left-to-right order (ag-Grid
 * splits a row into left-pinned / center / right-pinned sections, each a separate element sharing
 * the same `row-id`; most consumers pass a single-element array). For `"inside"`, only the first
 * and last section get the open-edge treatment (`indicatorInsideSectionLeft`/`-Right`) so the
 * frame reads as one continuous outline across sections — see `dropIndicator.module.scss`'s own
 * comment on why this can't just be `.dropInside`.
 *
 * Always call `clearElementIndicators()` first (or on every hover-changed tick) — this function
 * only adds classes, it never removes the previous target's.
 */
export function applyElementIndicator(
  elements: ArrayLike<Element>,
  side: ElementIndicatorSide,
  edge?: ElementIndicatorEdge,
): void {
  const total = elements.length;
  for (let index = 0; index < total; index++) {
    const el = elements[index];
    if (side === "above") {
      el.classList.add(styles.indicatorAbove);
      if (edge === "start") el.classList.add(styles.indicatorEdge);
    } else if (side === "below") {
      el.classList.add(styles.indicatorBelow);
      if (edge === "end") el.classList.add(styles.indicatorEdge);
    } else {
      el.classList.add(styles.indicatorInsideSection);
      if (index === 0) el.classList.add(styles.indicatorInsideSectionLeft);
      if (index === total - 1) el.classList.add(styles.indicatorInsideSectionRight);
    }
  }
}

/** Sweeps `root` (default: the whole document) for every element carrying any indicator class from
 *  `applyElementIndicator` and removes them all — cheaper and more robust than tracking which
 *  elements were previously touched, especially for ag-Grid's recycled rows, where the element a
 *  class was added to may no longer be the one currently showing that data. */
export function clearElementIndicators(root: ParentNode = document): void {
  root.querySelectorAll(ALL_CLASSES.map((c) => `.${c}`).join(",")).forEach((el) => {
    el.classList.remove(...ALL_CLASSES);
  });
}
